import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { jobsAPI, partsAPI, invoicesAPI } from "../lib/api";
import { compressImage } from "../lib/imageUtils";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Import sub-components
import {
  JobDetailHeader,
  JobDetailInfo,
  JobDetailMeta,
  JobDetailWorkSection,
  JobDetailPartsSection,
  JobDetailPhotosSection,
  JobDetailCostSummary,
  JobDetailSignature,
  JobDetailCheckInStatus,
  JobDetailCheckInButtons,
  PhotoWarningDialog,
  SerialNumberWarningDialog,
  WorkPhotoWarningDialog,
  SignatureWarningDialog,
  PhotoLightbox,
} from "../components/job-detail";

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Core state
  const [job, setJob] = useState(null);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Dialog states
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [partsDialogOpen, setPartsDialogOpen] = useState(false);
  const [photoWarningOpen, setPhotoWarningOpen] = useState(false);
  const [serialNumberWarningOpen, setSerialNumberWarningOpen] = useState(false);
  const [workPhotoWarningOpen, setWorkPhotoWarningOpen] = useState(false);
  const [signatureWarningOpen, setSignatureWarningOpen] = useState(false);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);

  // Form states
  const [workNotes, setWorkNotes] = useState("");
  const [labourCharge, setLabourCharge] = useState(0);
  const [mechanicSerialNumber, setMechanicSerialNumber] = useState("");
  const [serialNumberPhoto, setSerialNumberPhoto] = useState(null);
  const [serialNumberSectionPhoto, setSerialNumberSectionPhoto] = useState(null);
  const [checkoutWorkPhotos, setCheckoutWorkPhotos] = useState({});
  const [selectedPart, setSelectedPart] = useState("");
  const [partQuantity, setPartQuantity] = useState(1);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [missingPhotoTypes, setMissingPhotoTypes] = useState([]);

  // Computed values
  const isMechanic = user?.role === "mechanic";
  const canEdit = user?.role === "admin" || user?.role === "manager" || user?.id === job?.assigned_mechanic_id;
  const canEditCosts = user?.role === "admin" || user?.role === "manager";
  const canCreateInvoice = (user?.role === "admin" || user?.role === "manager") && job?.status === "completed";

  // Fetch job data
  const fetchJob = useCallback(async () => {
    try {
      const res = await jobsAPI.getOne(jobId);
      setJob(res.data);
      setWorkNotes(res.data.work_notes || "");
      setLabourCharge(res.data.labour_charge || 0);
      setMechanicSerialNumber(res.data.mechanic_serial_number || "");
    } catch (error) {
      toast.error("Failed to load job");
      navigate("/jobs");
    } finally {
      setLoading(false);
    }
  }, [jobId, navigate]);

  // Fetch parts
  const fetchParts = useCallback(async () => {
    try {
      const res = await partsAPI.getAll();
      setParts(res.data);
    } catch (error) {
      console.error("Failed to fetch parts");
    }
  }, []);

  useEffect(() => {
    fetchJob();
    fetchParts();
  }, [fetchJob, fetchParts]);

  // GPS location helper
  const getGPSLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  // Update job handler
  const handleUpdateJob = async (updates) => {
    try {
      setUpdating(true);
      await jobsAPI.update(jobId, updates);
      await fetchJob();
      return true;
    } catch (error) {
      toast.error("Failed to update job");
      return false;
    } finally {
      setUpdating(false);
    }
  };

  // Auto-save serial number
  const autoSaveSerialNumber = useCallback(
    async (serialNum, photo) => {
      if (!serialNum?.trim()) return;
      const updateData = { mechanic_serial_number: serialNum.trim() };
      if (photo) updateData.serial_number_photo = photo;
      try {
        await jobsAPI.update(jobId, updateData);
      } catch (error) {
        // Silent fail for auto-save
      }
    },
    [jobId]
  );

  // Check if all required photos are present
  const hasRequiredPhotos = () => {
    const jobTypes = job?.job_type_tags || [];
    if (jobTypes.length === 0) {
      return Object.values(checkoutWorkPhotos).flat().length >= 1;
    }
    return jobTypes.every((tag) => (checkoutWorkPhotos[tag] || []).length >= 1);
  };

  // Check-in handler
  const handleCheckIn = async () => {
    if (!serialNumberPhoto) {
      setPhotoWarningOpen(true);
      return;
    }

    try {
      setGettingLocation(true);
      const location = await getGPSLocation();
      setGpsLocation(location);

      await jobsAPI.checkIn(jobId, {
        latitude: location.latitude,
        longitude: location.longitude,
        serial_number_photo: serialNumberPhoto,
      });

      toast.success("Checked in successfully");
      fetchJob();
    } catch (error) {
      toast.error("Failed to check in");
    } finally {
      setGettingLocation(false);
    }
  };

  // Check-out handler
  const handleCheckOut = async () => {
    // Validate serial number
    if (!mechanicSerialNumber?.trim() && !job?.mechanic_serial_number) {
      setSerialNumberWarningOpen(true);
      return;
    }

    // Validate serial number photo
    if (!serialNumberSectionPhoto && !job?.serial_number_photo) {
      toast.error("Please upload a photo of the serial number plate");
      return;
    }

    // Validate work photos
    const jobTypes = job?.job_type_tags || [];
    if (jobTypes.length > 0) {
      const missing = jobTypes.filter((tag) => (checkoutWorkPhotos[tag] || []).length < 1);
      if (missing.length > 0) {
        setMissingPhotoTypes(missing);
        setWorkPhotoWarningOpen(true);
        return;
      }
    } else if (Object.values(checkoutWorkPhotos).flat().length < 1) {
      setWorkPhotoWarningOpen(true);
      return;
    }

    // Validate signature
    if (!job?.customer_signature) {
      setSignatureWarningOpen(true);
      return;
    }

    try {
      setGettingLocation(true);
      const location = await getGPSLocation();

      await jobsAPI.checkOut(jobId, {
        latitude: location.latitude,
        longitude: location.longitude,
        work_photos: Object.values(checkoutWorkPhotos).flat(),
        work_photos_by_type: checkoutWorkPhotos,
      });

      toast.success("Checked out successfully");
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check out");
    } finally {
      setGettingLocation(false);
    }
  };

  // Add part handler
  const handleAddPart = async (partData) => {
    const currentParts = job.parts_used || [];
    const existingIndex = currentParts.findIndex((p) => p.part_id === partData.part_id);

    let newParts;
    if (existingIndex >= 0) {
      newParts = [...currentParts];
      newParts[existingIndex].quantity += partData.quantity;
    } else {
      newParts = [...currentParts, partData];
    }

    const success = await handleUpdateJob({ parts_used: newParts });
    if (success) toast.success("Part added");
  };

  // Remove part handler
  const handleRemovePart = async (index) => {
    const newParts = (job.parts_used || []).filter((_, i) => i !== index);
    const success = await handleUpdateJob({ parts_used: newParts });
    if (success) toast.success("Part removed");
  };

  // Update labour charge
  const handleUpdateLabourCharge = async () => {
    if (labourCharge !== job.labour_charge) {
      await handleUpdateJob({ labour_charge: labourCharge });
    }
  };

  // Save signature
  const handleSaveSignature = async (signatureData) => {
    const success = await handleUpdateJob({ customer_signature: signatureData });
    if (success) toast.success("Signature saved");
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    try {
      await invoicesAPI.create({ job_id: jobId });
      toast.success("Invoice created");
      navigate("/invoices");
    } catch (error) {
      toast.error("Failed to create invoice");
    }
  };

  // Reference photo handlers
  const handleUploadReferencePhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentPhotos = job.photos || [];
    if (currentPhotos.length + files.length > 20) {
      toast.error("Maximum 20 photos allowed");
      return;
    }

    try {
      const compressedPhotos = await Promise.all(files.map((f) => compressImage(f)));
      const success = await handleUpdateJob({ photos: [...currentPhotos, ...compressedPhotos] });
      if (success) toast.success(`${files.length} photo(s) uploaded`);
    } catch (error) {
      toast.error("Failed to upload photos");
    }
  };

  const handleDeleteReferencePhoto = async (index) => {
    const newPhotos = (job.photos || []).filter((_, i) => i !== index);
    const success = await handleUpdateJob({ photos: newPhotos });
    if (success) toast.success("Photo deleted");
  };

  // Serial number photo for check-in
  const handleSerialNumberPhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setSerialNumberPhoto(compressed);
      toast.success("Serial number photo captured");
    } catch (error) {
      toast.error("Failed to capture photo");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // No job found
  if (!job) return null;

  return (
    <div data-testid="job-detail" className="space-y-6">
      {/* Warning Dialogs */}
      <PhotoWarningDialog open={photoWarningOpen} onOpenChange={setPhotoWarningOpen} />
      <SerialNumberWarningDialog open={serialNumberWarningOpen} onOpenChange={setSerialNumberWarningOpen} />
      <WorkPhotoWarningDialog
        open={workPhotoWarningOpen}
        onOpenChange={setWorkPhotoWarningOpen}
        missingTypes={missingPhotoTypes}
      />
      <SignatureWarningDialog open={signatureWarningOpen} onOpenChange={setSignatureWarningOpen} />
      <PhotoLightbox photo={enlargedPhoto} onClose={() => setEnlargedPhoto(null)} />

      {/* Header */}
      <JobDetailHeader job={job} onBack={() => navigate("/jobs")} />

      {/* Mechanic Check-in/out Section */}
      {isMechanic && (job.status === "pending" || job.status === "in_progress") && (
        <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-800">
                {job.check_in ? "Ready to Check Out" : "Ready to Check In"}
              </h3>
              <p className="text-sm text-slate-500">
                {job.check_in
                  ? "Complete work photos and get customer signature before checking out"
                  : "Take a photo of the serial number plate to check in"}
              </p>
            </div>

            {/* Check-in photo capture (before check-in) */}
            {!job.check_in && (
              <div className="flex items-center gap-3">
                <input
                  id="checkin-serial-photo"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleSerialNumberPhotoCapture}
                />
                <button
                  onClick={() => document.getElementById("checkin-serial-photo").click()}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-sm text-sm font-medium"
                >
                  {serialNumberPhoto ? "✓ Photo Captured" : "📷 Capture Serial Photo"}
                </button>
              </div>
            )}

            <JobDetailCheckInButtons
              job={job}
              isMechanic={isMechanic}
              gettingLocation={gettingLocation}
              onCheckIn={handleCheckIn}
              onCheckOut={handleCheckOut}
              hasRequiredPhotos={hasRequiredPhotos()}
              hasSerialNumber={!!(mechanicSerialNumber || job.mechanic_serial_number)}
              hasSignature={!!job.customer_signature}
            />
          </div>
        </div>
      )}

      {/* Check-in/out Status */}
      <JobDetailCheckInStatus job={job} />

      {/* Details Card */}
      <JobDetailInfo job={job} />

      {/* Job Information */}
      <JobDetailMeta job={job} />

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Work Details, Parts, Photos */}
        <div className="lg:col-span-2 space-y-6">
          <JobDetailWorkSection
            job={job}
            isMechanic={isMechanic}
            canEdit={canEdit}
            workNotes={workNotes}
            setWorkNotes={setWorkNotes}
            mechanicSerialNumber={mechanicSerialNumber}
            setMechanicSerialNumber={setMechanicSerialNumber}
            serialNumberSectionPhoto={serialNumberSectionPhoto}
            setSerialNumberSectionPhoto={setSerialNumberSectionPhoto}
            onUpdateJob={handleUpdateJob}
            onAutoSaveSerialNumber={autoSaveSerialNumber}
          />

          <JobDetailPartsSection
            job={job}
            parts={parts}
            isMechanic={isMechanic}
            canEditCosts={canEditCosts}
            labourCharge={labourCharge}
            setLabourCharge={setLabourCharge}
            selectedPart={selectedPart}
            setSelectedPart={setSelectedPart}
            partQuantity={partQuantity}
            setPartQuantity={setPartQuantity}
            partsDialogOpen={partsDialogOpen}
            setPartsDialogOpen={setPartsDialogOpen}
            onAddPart={handleAddPart}
            onRemovePart={handleRemovePart}
            onUpdateLabourCharge={handleUpdateLabourCharge}
          />

          <JobDetailPhotosSection
            job={job}
            isMechanic={isMechanic}
            canEdit={canEdit}
            checkoutWorkPhotos={checkoutWorkPhotos}
            setCheckoutWorkPhotos={setCheckoutWorkPhotos}
            onUploadReferencePhoto={handleUploadReferencePhoto}
            onDeleteReferencePhoto={handleDeleteReferencePhoto}
            onEnlargePhoto={setEnlargedPhoto}
          />
        </div>

        {/* Right Column - Cost Summary & Signature */}
        <div className="space-y-6">
          <JobDetailCostSummary
            job={job}
            isMechanic={isMechanic}
            canCreateInvoice={canCreateInvoice}
            onCreateInvoice={handleCreateInvoice}
          />

          <JobDetailSignature
            job={job}
            isMechanic={isMechanic}
            canEdit={canEdit}
            signatureDialogOpen={signatureDialogOpen}
            setSignatureDialogOpen={setSignatureDialogOpen}
            onSaveSignature={handleSaveSignature}
          />
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
